import { CycleDetailHeading } from "@domains/reviews/client/components/cycles/detail/CycleDetailHeading.tsx";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import { PERFORMANCE_CYCLES_ACCESS } from "@packages/core/access/settingsAccess.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useEnvironmentVariable, useFeatureFlags } from "@squide/firefly";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMatches, useNavigate } from "react-router";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { cycleOriginPath, type CycleDetailOrigin } from "@domains/reviews/client/components/cycles/cycleDetailPaths.ts";
import { useCycleReturnOrigin } from "@domains/reviews/client/components/cycles/useCycleReturnOrigin.ts";
import { resolveCycleFormTargets } from "@domains/reviews/client/components/cycles/cycleFormTargets.ts";

export interface ReviewCycleDetailHeadingProps {
    origin: CycleDetailOrigin;
}

export function ReviewCycleDetailHeading({ origin }: ReviewCycleDetailHeadingProps) {
    const returnOrigin = useCycleReturnOrigin(origin);
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ReviewCycleDetailHeading", { i18n: i18nextInstance });
    const featureFlags = useFeatureFlags();
    const session = useSession();
    const navigate = useNavigate();
    const workleapPerformanceUrl = useEnvironmentVariable("workleapPerformanceUrl");

    const matches = useMatches();
    const cycleId = matches[matches.length - 1]?.params?.cycleId ?? "";

    const ancestorCrumbs = useMemo(() => [{ label: t(`origin.${returnOrigin}`), href: cycleOriginPath(returnOrigin) }], [returnOrigin, t]);

    const isBuilderEnabled = featureFlags?.["review-cycle-builder"] ?? false;

    const cycleForm = useMemo(() => resolveCycleFormTargets({ origin, isBuilderEnabled, workleapPerformanceUrl }), [isBuilderEnabled, origin, workleapPerformanceUrl]);

    const handleCycleDeleted = useCallback(() => navigate(cycleOriginPath(returnOrigin), { replace: true }), [navigate, returnOrigin]);

    const handleCycleNotViewable = useCallback(() => navigate(cycleOriginPath(returnOrigin), { replace: true }), [navigate, returnOrigin]);

    if (!cycleId) {
        return null;
    }

    return (
        <div className="-mt-4 w-full">
            <CycleDetailHeading
                cycleId={cycleId}
                ancestorCrumbs={ancestorCrumbs}
                canAdministerCycles={hasAnyRole(session, PERFORMANCE_CYCLES_ACCESS)}
                getEditCycleHref={cycleForm.getEditHref}
                getViewCycleHref={cycleForm.getViewHref}
                isEditInApp={cycleForm.isInApp}
                onCycleDeleted={handleCycleDeleted}
                onCycleNotViewable={handleCycleNotViewable}
            />
        </div>
    );
}
